import { NoteDTO, NOTE_SIZE, NOTE_CANVAS } from "@retrospecter/shared/types";

/** Breathing room kept between a note and the edges of its canvas. */
export const CANVAS_PADDING = 12;

/** How far one arrow-key press moves or resizes a note. */
export const NUDGE_STEP = 16;

/** A canvas never collapses below this, so an empty column is still a target. */
export const MIN_CANVAS_HEIGHT = 220;

export interface Placement {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Fits a note inside a canvas of `canvasWidth`.
 *
 * Coordinates are stored in pixels, but a column is narrower on a phone than
 * on a desktop, so a note saved on a wide screen would otherwise hang off the
 * edge of a narrow one. Clamping at render time keeps every note reachable
 * without rewriting what people arranged on a bigger screen.
 */
export function fitToCanvas(note: Placement, canvasWidth: number): Placement {
  // A canvas that has not been measured yet (first paint, or jsdom) must not
  // squash everything to the left edge.
  const width = canvasWidth > 0 ? Math.min(note.width, canvasWidth) : note.width;
  const maxX = canvasWidth > 0 ? Math.max(0, canvasWidth - width) : NOTE_CANVAS.maxX;

  return {
    x: Math.max(0, Math.min(note.x, maxX)),
    y: Math.max(0, Math.min(note.y, NOTE_CANVAS.maxY)),
    width,
    height: note.height,
  };
}

/** How tall a canvas has to be to hold everything on it. */
export function canvasHeightFor(notes: Placement[]): number {
  const bottom = notes.reduce(
    (lowest, note) => Math.max(lowest, note.y + note.height),
    0
  );
  return Math.max(MIN_CANVAS_HEIGHT, bottom + CANVAS_PADDING * 2);
}

function overlaps(a: Placement, b: Placement): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

/**
 * Picks somewhere to put a note nobody dropped by hand — one added from the
 * pile's column menu, which is the keyboard path and so has no drop point.
 *
 * Scans the canvas for the first free slot, and once the canvas is full falls
 * back to a cascade so late notes still land somewhere visible instead of
 * stacking exactly on top of each other.
 */
export function freeSpotFor(
  taken: Placement[],
  canvasWidth: number,
  size: { width: number; height: number } = {
    width: NOTE_SIZE.defaultWidth,
    height: NOTE_SIZE.defaultHeight,
  }
): { x: number; y: number } {
  const step = 24;
  const width = canvasWidth > 0 ? Math.min(size.width, canvasWidth) : size.width;
  const lastX = Math.max(CANVAS_PADDING, canvasWidth - width - CANVAS_PADDING);

  for (let y = CANVAS_PADDING; y < NOTE_CANVAS.maxY; y += step) {
    for (let x = CANVAS_PADDING; x <= lastX; x += step) {
      const candidate = { x, y, width, height: size.height };
      if (!taken.some((note) => overlaps(candidate, note))) return { x, y };
    }
  }

  const offset = (taken.length % 8) * step;
  return { x: CANVAS_PADDING + offset, y: CANVAS_PADDING + offset };
}

/**
 * Applies a placement to one note in a list, bringing it to the front.
 *
 * Used for the optimistic copy the board shows while the move is in flight —
 * a post-it that snapped back to where it started until the server answered
 * would make every drag feel broken.
 */
export function withPlacement(
  notes: NoteDTO[],
  noteId: string,
  placement: Partial<Placement> & { columnId?: string },
  toFront = true
): NoteDTO[] {
  const front = notes.reduce((highest, note) => Math.max(highest, note.position), 0);

  return notes.map((note) =>
    note.id === noteId
      ? { ...note, ...placement, ...(toFront && { position: front + 1 }) }
      : note
  );
}

/**
 * A free spot on a column's canvas, measured from the live DOM.
 *
 * Used for notes added without a pointer — from the pile's column menu, which
 * is the keyboard path and so has no drop point to place them at.
 */
export function freeSpotInColumn(
  columnId: string,
  taken: Placement[]
): { x: number; y: number } {
  const canvas = document.querySelector<HTMLElement>(
    `[data-column-canvas="${columnId}"]`
  );
  return freeSpotFor(taken, canvas?.getBoundingClientRect().width ?? 0);
}
